const MARGEM = 12;

const ROLA = new Set(["auto", "scroll", "overlay"]);

const rolavel = (el: HTMLElement) => ROLA.has(getComputedStyle(el).overflowY);

export const caixaDeRolagem = (el: HTMLElement) => {
    let no = el.parentElement;

    while (no && no !== document.body) {
        if (rolavel(no)) return no;

        no = no.parentElement;
    }

    return null;
};

export const deltaParaVista = (inicio: number, fim: number, limiteInicio: number, limiteFim: number) => {
    const antes = inicio - limiteInicio - MARGEM;
    const depois = fim - limiteFim + MARGEM;

    return antes < 0 || fim - inicio > limiteFim - limiteInicio ? antes : Math.max(depois, 0);
};

const comportamento = (): ScrollBehavior =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

export const rolarAteAVista = (el: HTMLElement) => {
    const caixa = caixaDeRolagem(el);

    if (!caixa) return;

    const alvo = el.getBoundingClientRect();
    const limite = caixa.getBoundingClientRect();

    const delta = deltaParaVista(alvo.top, alvo.bottom, limite.top, limite.bottom);

    if (Math.abs(delta) < 1) return;

    caixa.scrollTo({ top: caixa.scrollTop + delta, behavior: comportamento() });
};

type Caixa = {
    rolaX: boolean;
    rolaY: boolean;
    limite: { top: number; bottom: number; left: number; right: number };
    atual: { x: number; y: number };
    maximo: { x: number; y: number };
    rolar: (destino: { left: number; top: number }) => void;
};

const caixaDeElemento = (no: HTMLElement): Caixa => {
    const estilo = getComputedStyle(no);
    const limite = no.getBoundingClientRect();

    return {
        rolaX: ROLA.has(estilo.overflowX),
        rolaY: ROLA.has(estilo.overflowY),
        limite,
        atual: { x: no.scrollLeft, y: no.scrollTop },
        maximo: { x: no.scrollWidth - no.clientWidth, y: no.scrollHeight - no.clientHeight },
        rolar: (destino) => no.scrollTo({ ...destino, behavior: comportamento() }),
    };
};

const caixaDaJanela = (): Caixa => {
    const raiz = document.documentElement;

    return {
        rolaX: true,
        rolaY: true,
        limite: { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth },
        atual: { x: window.scrollX, y: window.scrollY },
        maximo: { x: raiz.scrollWidth - window.innerWidth, y: raiz.scrollHeight - window.innerHeight },
        rolar: (destino) => window.scrollTo({ ...destino, behavior: comportamento() }),
    };
};

type Retangulo = { top: number; bottom: number; left: number; right: number };

export type OpcoesDeCadeia = {
    janela?: boolean;
    folgaEmbaixo?: number;
};

const entre = (valor: number, maximo: number) => Math.min(Math.max(valor, 0), Math.max(maximo, 0));

export const planejarCadeia = (alvo: Retangulo, caixas: Caixa[], alturaDaTela: number, folgaEmbaixo = 0) => {
    let previsto = { top: alvo.top, bottom: alvo.bottom, left: alvo.left, right: alvo.right };
    const passos: { caixa: Caixa; left: number; top: number }[] = [];

    for (const caixa of caixas) {
        const { limite, atual, maximo } = caixa;
        const naFaixaDaBarra = folgaEmbaixo > 0 && limite.bottom >= alturaDaTela - folgaEmbaixo;
        const fundo = naFaixaDaBarra ? Math.min(limite.bottom, alturaDaTela - folgaEmbaixo) : limite.bottom;

        const dy = caixa.rolaY ? deltaParaVista(previsto.top, previsto.bottom, limite.top, fundo) : 0;
        const dx = caixa.rolaX ? deltaParaVista(previsto.left, previsto.right, limite.left, limite.right) : 0;

        const top = entre(atual.y + dy, maximo.y);
        const left = entre(atual.x + dx, maximo.x);
        const andouY = top - atual.y;
        const andouX = left - atual.x;

        if (Math.abs(andouY) < 1 && Math.abs(andouX) < 1) continue;

        passos.push({ caixa, left, top });

        previsto = {
            top: previsto.top - andouY,
            bottom: previsto.bottom - andouY,
            left: previsto.left - andouX,
            right: previsto.right - andouX,
        };
    }

    return { passos, previsto };
};

export const rolarCadeiaAteAVista = (el: Element, { janela = false, folgaEmbaixo = 0 }: OpcoesDeCadeia = {}) => {
    const caixas: Caixa[] = [];
    let no = el.parentElement;

    while (no && no !== document.body && no !== document.documentElement) {
        const estilo = getComputedStyle(no);

        if (ROLA.has(estilo.overflowY) || ROLA.has(estilo.overflowX)) caixas.push(caixaDeElemento(no));

        no = no.parentElement;
    }

    if (janela) caixas.push(caixaDaJanela());

    const { passos } = planejarCadeia(el.getBoundingClientRect(), caixas, window.innerHeight, folgaEmbaixo);

    for (const passo of passos) passo.caixa.rolar({ left: passo.left, top: passo.top });
};
